import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaUsers, FaCheckCircle, FaEuroSign, FaRobot, FaClipboardCheck } from 'react-icons/fa';
import { getBrand } from '../utils/brandStyles';
import PageHero from '../components/PageHero';
import CTABanner from '../components/CTABanner';

const SmallBusinessSolution = ({ market = 'lu' }) => {
  const b = getBrand(market);

  // 6 challenges → 3-col grid
  const challenges = [
    'Limited IT budget and resources',
    'No in-house technical expertise',
    'Using outdated or inefficient tools',
    'Security concerns with no budget for expensive solutions',
    'Difficulty competing with larger companies',
    'Unreliable backups and disaster recovery'
  ];

  // 4 solutions → 2×2 grid
  const solutions = [
    {
      title: 'Fast Cloud Migration',
      description: 'Get to Microsoft 365 or Google Workspace in days, not months.',
      price: 'Starting at €500'
    },
    {
      title: 'Essential Security Setup',
      description: 'GDPR-compliant security basics: backups, antivirus, password management.',
      price: 'From €300/month'
    },
    {
      title: 'AI Quick Wins',
      description: 'Simple automation to save 5–10 hours per week on repetitive tasks.',
      price: 'Project-based'
    },
    {
      title: 'On-Demand IT Support',
      description: 'Pay only for what you use — no monthly retainers.',
      price: '€80/hour'
    }
  ];

  // 6 why us → 3-col grid
  const whyUs = [
    'Transparent, affordable pricing',
    'No long-term contracts required',
    'Fast turnaround — solutions in days',
    'Clear communication, no jargon',
    'Focus on immediate ROI',
    'Flexible support — pay only for what you need'
  ];

  // 4 assessment items → 2×2 grid
  const assessmentItems = [
    'Current cloud usage and potential savings',
    'Security vulnerabilities and compliance gaps',
    'Opportunities for AI and automation',
    'Backup and disaster recovery readiness'
  ];

  // 4 AI items → 2×2 grid
  const aiItems = [
    'Customer service chatbots',
    'Document processing automation',
    'Email sorting and auto-responses',
    'Data entry and synchronisation'
  ];

  return (
    <div style={{ fontFamily: b.fontBody, color: b.text, background: b.bg }}>

      {/* ── HERO ── */}
      <PageHero
        market={market}
        title="IT Solutions for Small Businesses"
        subtitle="Professional IT services tailored for small teams and growing businesses. Affordable, fast, and practical solutions that scale with you."
        ctaText="Get Free Assessment"
        ctaLink="/services/assessment"
        icon={<FaUsers color="#1A237E" />}
      />

      {/* ── CHALLENGES — 6 → 3-col ── */}
      <section style={{ background: b.bgAlt, padding: b.secPad }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <span className="eyebrow" style={{ color: b.isMWO ? '#FF6B6B' : '#DC2626' }}>Common Pain Points</span>
            <h2 style={{ fontFamily: b.fontDisplay, fontSize: b.h2size, color: b.textHeading, letterSpacing: '-0.02em' }}>
              Challenges we solve
            </h2>
          </div>
          <div className="grid-6">
            {challenges.map((challenge, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, x: -16 }} whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.35, delay: i * 0.06 }} viewport={{ once: true }}
                style={{
                  display: 'flex', gap: '12px', alignItems: 'flex-start',
                  background: b.isMWO ? '#1a0f0f' : '#FFF5F5',
                  borderRadius: b.cardRadius,
                  padding: '20px',
                  borderLeft: b.isMWO ? '3px solid #FF6B6B' : '3px solid #FCA5A5'
                }}
              >
                <span style={{ color: b.isMWO ? '#FF6B6B' : '#DC2626', flexShrink: 0, fontSize: '1rem', marginTop: '2px' }}>✕</span>
                <span style={{ fontSize: b.fz.sm, lineHeight: 1.6, color: b.isMWO ? 'rgba(255,213,213,0.75)' : '#6B2121' }}>{challenge}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SOLUTIONS — 4 → 2×2 ── */}
      <section style={{ background: b.bg, padding: b.secPad }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <span className="eyebrow" style={{ color: b.eyebrowColor }}>What We Offer</span>
            <h2 style={{ fontFamily: b.fontDisplay, fontSize: b.h2size, color: b.textHeading, letterSpacing: '-0.02em' }}>
              Affordable solutions that work
            </h2>
          </div>
          <div className="grid-4">
            {solutions.map((sol, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.07 }} viewport={{ once: true }}
                className={b.isMWO ? 'feature-card mwo-card' : 'feature-card mindit-card'}
                style={{ background: b.bgCard, borderRadius: b.cardRadius, boxShadow: b.cardShadow, padding: '32px 24px', textAlign: 'center' }}
              >
                <FaCheckCircle size={36} color="#4CAF50" style={{ marginBottom: '16px' }} />
                <h3 style={{ fontFamily: b.fontDisplay, fontSize: b.fz.lg, color: b.textHeading, marginBottom: '10px' }}>{sol.title}</h3>
                <p style={{ fontSize: b.fz.sm, lineHeight: 1.7, color: b.textMuted, marginBottom: '16px' }}>{sol.description}</p>
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: '5px',
                  background: b.isMWO ? 'rgba(0,188,212,0.12)' : '#EFF6FF',
                  color: b.accent, padding: '6px 14px',
                  borderRadius: b.isMWO ? '0' : '20px',
                  fontSize: b.fz.xs, fontWeight: 700
                }}>
                  <FaEuroSign size={12} /> {sol.price}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHY US — 6 → 3-col ── */}
      <section style={{ background: b.bgAlt, padding: b.secPad }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <span className="eyebrow" style={{ color: b.eyebrowColor }}>Why Choose Us</span>
            <h2 style={{ fontFamily: b.fontDisplay, fontSize: b.h2size, color: b.textHeading, letterSpacing: '-0.02em' }}>
              Why small businesses choose us
            </h2>
          </div>
          <div className="grid-6">
            {whyUs.map((reason, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, x: -16 }} whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.35, delay: i * 0.06 }} viewport={{ once: true }}
                className="checklist-item"
                style={{ background: b.checklistBg, borderRadius: b.isMWO ? '0' : '8px', borderLeft: b.isMWO ? `3px solid ${b.accent}` : 'none' }}
              >
                <FaCheckCircle size={16} color={b.accent} className="checklist-icon" />
                <span style={{ fontSize: b.fz.sm, lineHeight: 1.6, fontWeight: 500 }}>{reason}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FREE ASSESSMENT — 4 items → 2×2 ── */}
      <section style={{ background: b.bg, padding: b.secPad }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '40px' }}>
            <FaClipboardCheck size={40} color={b.accent} style={{ marginBottom: '16px' }} />
            <span className="eyebrow" style={{ color: b.eyebrowColor }}>Free Assessment</span>
            <h2 style={{ fontFamily: b.fontDisplay, fontSize: b.h2size, color: b.textHeading, letterSpacing: '-0.02em', marginBottom: '10px' }}>
              Start with a free IT assessment
            </h2>
            <p style={{ color: b.textMuted, maxWidth: '500px', margin: '0 auto', fontSize: b.fz.base }}>
              Not sure where to start? Get a comprehensive review with actionable recommendations — completely free.
            </p>
          </div>
          <div className="grid-4" style={{ marginBottom: '40px' }}>
            {assessmentItems.map((item, i) => (
              <div key={i} className="checklist-item"
                style={{ background: b.checklistBg, borderRadius: b.isMWO ? '0' : '8px', borderLeft: b.isMWO ? `3px solid ${b.accent}` : 'none' }}>
                <FaCheckCircle size={16} color={b.accent} className="checklist-icon" />
                <span style={{ fontSize: b.fz.sm, lineHeight: 1.6 }}>{item}</span>
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'center' }}>
            <Link to="/services/assessment" className={b.ctaBtnClass}>
              Learn More About Free Assessment
            </Link>
          </div>
        </div>
      </section>

      {/* ── AI AUTOMATION — 4 items → 2×2 ── */}
      <section style={{ background: b.bgAlt, padding: b.secPad }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '40px' }}>
            <FaRobot size={40} color={b.accent} style={{ marginBottom: '16px' }} />
            <span className="eyebrow" style={{ color: b.eyebrowColor }}>AI & Automation</span>
            <h2 style={{ fontFamily: b.fontDisplay, fontSize: b.h2size, color: b.textHeading, letterSpacing: '-0.02em', marginBottom: '10px' }}>
              AI-powered automation for small teams
            </h2>
            <p style={{ color: b.textMuted, maxWidth: '500px', margin: '0 auto', fontSize: b.fz.base }}>
              Save 5–10 hours per week with practical AI solutions that automate repetitive tasks.
            </p>
          </div>
          <div className="grid-4" style={{ marginBottom: '40px' }}>
            {aiItems.map((item, i) => (
              <div key={i} className="checklist-item"
                style={{ background: b.checklistBg, borderRadius: b.isMWO ? '0' : '8px', borderLeft: b.isMWO ? `3px solid ${b.accent}` : 'none' }}>
                <FaRobot size={16} color={b.accent} className="checklist-icon" />
                <span style={{ fontSize: b.fz.sm, lineHeight: 1.6 }}>{item}</span>
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'center' }}>
            <Link to="/services/ai-consulting" className={b.ctaBtnClass}>
              Explore AI Consulting
            </Link>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <CTABanner
        market={market}
        title="Let's build your tech foundation"
        body="Start with a free assessment. No commitment, no pressure — just honest advice."
        ctaText="Contact Us Today"
        ctaLink="/contact"
      />
    </div>
  );
};

export default SmallBusinessSolution;
